import { FC, useRef, useEffect } from "react";
import { Box, InputBase, Button, Paper, IconButton, Typography, Chip, CircularProgress, Tooltip, useMediaQuery } from "@mui/material";
import { AutoAwesome, Close, InsertDriveFile, Image, Description, TableChart } from "@mui/icons-material";
import { FiPlus, FiArrowUp } from "react-icons/fi";
import { PendingAttachment } from "@/hooks/useChatAttachmentUpload";
import { tokens } from "@/tokens";

// Allowed MIME types for file input
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
].join(",");

interface ChatInputAreaProps {
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  // Attachment props
  pendingAttachments?: PendingAttachment[];
  onFileSelect?: (file: File) => void;
  onRemoveAttachment?: (id: string) => void;
  isUploading?: boolean;
  chatId?: string | null;
}

// Get icon for file type
const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith("image/")) {
    return <Image sx={{ fontSize: tokens.fontSize.lg, color: "#687071" }} />;
  }
  if (mimeType === "application/pdf") {
    return <Description sx={{ fontSize: tokens.fontSize.lg, color: "#E53935" }} />;
  }
  if (
    mimeType.includes("spreadsheet") ||
    mimeType === "text/csv"
  ) {
    return <TableChart sx={{ fontSize: tokens.fontSize.lg, color: "#43A047" }} />;
  }
  if (mimeType.includes("wordprocessing")) {
    return <Description sx={{ fontSize: tokens.fontSize.lg, color: "#1976D2" }} />;
  }
  return <InsertDriveFile sx={{ fontSize: tokens.fontSize.lg, color: "#687071" }} />;
};

// Get status color
const getStatusColor = (status: PendingAttachment["status"]) => {
  switch (status) {
    case "uploading":
      return "#1976D2";
    case "uploaded":
      return "#43A047"; // Green for uploaded (ready to send)
    case "processing":
      return "#FF9800";
    case "ready":
      return "#43A047";
    case "error":
      return "#E53935";
    default:
      return "#687071";
  }
};

const ChatInputArea: FC<ChatInputAreaProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  isLoading,
  pendingAttachments = [],
  onFileSelect,
  onRemoveAttachment,
  isUploading = false,
  chatId: _chatId, // Reserved for future use
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobileWidth = useMediaQuery('(max-width:600px)');
  const isMessageEmpty = value.trim() === "";
  const hasAttachments = pendingAttachments.length > 0;
  const canSend = (!isMessageEmpty || hasAttachments) && !isLoading && !isUploading;

  // Keyboard-aware scrolling for mobile
  useEffect(() => {
    if (!isMobileWidth) return;

    const handleResize = () => {
      // When virtual keyboard opens, scroll input into view
      if (window.visualViewport && inputRef.current && document.activeElement === inputRef.current) {
        setTimeout(() => {
          inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    return () => window.visualViewport?.removeEventListener('resize', handleResize);
  }, [isMobileWidth]);

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        maxWidth: "800px",
        px: 4,
        pb: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          border: "1px solid #E6E7E7",
          borderRadius: tokens.radius.lg,
          p: 1.5,
          backgroundColor: tokens.bg.surface,
        }}
      >
        {/* Attachment Preview Area */}
        {hasAttachments && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 1.5,
              pb: 1.5,
              borderBottom: "1px solid #E6E7E7",
            }}
          >
            {pendingAttachments.map((attachment) => (
              <Chip
                key={attachment.id}
                icon={
                  attachment.status === "uploading" || attachment.status === "processing" ? (
                    <CircularProgress size={isMobileWidth ? 14 : 12} sx={{ color: getStatusColor(attachment.status) }} />
                  ) : (
                    getFileIcon(attachment.file.type)
                  )
                }
                label={
                  <Tooltip title={
                    attachment.error
                      ? attachment.error
                      : attachment.status === "uploaded"
                      ? `${attachment.file.name} - Ready to send`
                      : attachment.status === "processing"
                      ? `${attachment.file.name} - Processing...`
                      : attachment.file.name
                  }>
                    <Typography
                      sx={{
                        fontSize: isMobileWidth ? tokens.fontSize.md : tokens.fontSize.base,
                        maxWidth: isMobileWidth ? 100 : 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {attachment.file.name}
                    </Typography>
                  </Tooltip>
                }
                onDelete={
                  onRemoveAttachment && !isLoading
                    ? () => onRemoveAttachment(attachment.id)
                    : undefined
                }
                deleteIcon={<Close sx={{ fontSize: isMobileWidth ? 18 : 14 }} />}
                size={isMobileWidth ? "medium" : "small"}
                sx={{
                  backgroundColor: attachment.status === "error" ? "#FFEBEE" : "#F5F5F5",
                  borderColor: getStatusColor(attachment.status),
                  borderWidth: 1,
                  borderStyle: "solid",
                  height: isMobileWidth ? 36 : 'auto',
                  "& .MuiChip-label": {
                    px: isMobileWidth ? 1 : 0.75,
                  },
                  "& .MuiChip-deleteIcon": {
                    color: "#9E9E9E",
                    minWidth: isMobileWidth ? 24 : 'auto',
                    minHeight: isMobileWidth ? 24 : 'auto',
                    "&:hover": {
                      color: "#616161",
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Input Field */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box sx={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AutoAwesome sx={{ fontSize: tokens.fontSize.xl, color: "#687071" }} />
          </Box>
          <InputBase
            inputRef={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyPress}
            placeholder={isLoading ? "Type your next message — Send unlocks when the agent finishes…" : hasAttachments ? "Add a message about your file..." : "Ask anything..."}
            // Intentionally not disabled while loading: the user should
            // be able to draft their next prompt while the agent is
            // still streaming. The Send button gates actual delivery
            // until canSend (the agent has emitted `done`).
            multiline
            maxRows={4}
            sx={{
              flex: 1,
              fontFamily: "Inter, sans-serif",
              fontSize: tokens.fontSize.lg,
              color: "#031112",
            }}
          />
        </Box>

        {/* Bottom Toolbar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {/* Plus Button - now opens file picker */}
            <Tooltip title="Attach file (PDF, Image, Excel, CSV, Word)">
              <span>
                <IconButton
                  size={isMobileWidth ? "medium" : "small"}
                  onClick={handleFileButtonClick}
                  disabled={isUploading}
                  sx={{
                    border: "1px solid #E8EBED",
                    borderRadius: tokens.radius.md,
                    p: isMobileWidth ? 1 : 0.5,
                    minWidth: isMobileWidth ? 44 : 'auto',
                    minHeight: isMobileWidth ? 44 : 'auto',
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                      borderColor: tokens.brand.primary,
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  }}
                >
                  {isUploading ? (
                    <CircularProgress size={isMobileWidth ? 22 : 18} sx={{ color: "#687071" }} />
                  ) : (
                    <FiPlus color="#687071" size={isMobileWidth ? 22 : 18} />
                  )}
                </IconButton>
              </span>
            </Tooltip>

          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={onSend}
              disabled={!canSend}
              sx={{
                background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, ${tokens.brand.primary} 0%, ${tokens.brand.primary} 100%)`,
                borderRadius: tokens.radius.md,
                color: "white",
                minWidth: isMobileWidth ? 44 : "unset",
                minHeight: isMobileWidth ? 44 : "unset",
                p: isMobileWidth ? "10px 16px" : "6px 12px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  background: `linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(90deg, ${tokens.brand.primaryHover} 0%, ${tokens.brand.primaryHover} 100%)`,
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                  background: "#E8EBED",
                  color: "#8E9494",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <FiArrowUp size={isMobileWidth ? 20 : 18} />
                <Typography sx={{ color: "inherit", fontWeight: tokens.fontWeight.semibold, fontSize: isMobileWidth ? "15px" : tokens.fontSize.lg }}>Send</Typography>
              </Box>
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
        <Typography sx={{ fontSize: tokens.fontSize.base, color: "#8E9494" }}>
          Riffs AI may display inaccurate info, so please double check the response.
        </Typography>
        <a href="https://tryriffs.com/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
          <Typography sx={{ fontSize: tokens.fontSize.base, color: "#33404D", textDecoration: "underline", fontWeight: tokens.fontWeight.semibold, cursor: "pointer" }}>
            Your Privacy & Riffs Intelligence
          </Typography>
        </a>
      </Box>
    </Box>
  );
};

export default ChatInputArea;
